import axios from "axios";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaStore } from "react-icons/fa";
import {
  FiUser,
  FiMapPin,
  FiCalendar,
  FiBookOpen,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiArrowLeft,
  FiSave
} from "react-icons/fi";
import Swal from "sweetalert2";

const CreateKaryawan = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullname: "",
    tempat_lahir: "",
    tanggal_lahir: "",
    alamat: "",
    gender: "Laki-laki",
    pendidikan: "",
    no_hp: "",
    email: "",
    join_date: "",
    penempatan: "",
  });

  const [errors, setErrors] = useState({});
  const [outlets, setOutlets] = useState([]);

  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  // Fetch Outlets
  useEffect(() => {
    const fetchOutlets = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/v1/outlet`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const raw = response.data?.data?.data || response.data?.data || response.data || [];
        setOutlets(Array.isArray(raw) ? raw : []);
      } catch (error) {
        console.error("Error mengambil outlet:", error);
      }
    };

    fetchOutlets();
  }, [token]);

  // Handle Input Change + Live Validation
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (!value) {
      setErrors((prev) => ({ ...prev, [name]: `${name} wajib diisi` }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    if (name === "email" && value) {
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      setErrors((prev) => ({ ...prev, email: valid ? "" : "Email tidak valid" }));
    }

    if (name === "no_hp" && value) {
      const valid = /^[0-9+]{8,15}$/.test(value);
      setErrors((prev) => ({
        ...prev,
        no_hp: valid ? "" : "Nomor HP tidak valid",
      }));
    }
  };

  // Validate All Before Submit
  const validateForm = () => {
    let newErrors = {};

    Object.keys(formData).forEach((key) => {
      if (!formData[key] && key !== "penempatan") {
        newErrors[key] = `Wajib diisi`;
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Email tidak valid";
    }
    if (formData.no_hp && !/^[0-9+]{8,15}$/.test(formData.no_hp)) {
      newErrors.no_hp = "Nomor HP tidak valid";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: "Form Belum Lengkap",
        text: "Mohon lengkapi semua kolom bertanda merah.",
        icon: "warning",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        ...formData,
        penempatan: formData.penempatan ? parseInt(formData.penempatan, 10) : null,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/employee`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.status === 201) {
        await Swal.fire({
          title: "Berhasil Ditambahkan!",
          text: `Karyawan "${formData.fullname}" berhasil disimpan.`,
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });

        navigate("/enterprise/karyawan");
      }
    } catch (error) {
      console.error("Gagal menambahkan karyawan:", error);
      Swal.fire({
        title: "Gagal Menyimpan",
        text: error.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 pb-12">
      {/* Header Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/enterprise/karyawan")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Buat Karyawan Baru
            </h1>
            <p className="text-xs text-gray-500">
              Isi data lengkap identitas dan penempatan outlet karyawan baru
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Card Form */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="bg-white rounded-3xl p-6 md:p-8 shadow-xl shadow-gray-200/50 border border-gray-100"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nama Lengkap */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiUser className="text-blue-600 w-4 h-4" />
                Nama Lengkap
              </label>
              <input
                type="text"
                name="fullname"
                value={formData.fullname}
                onChange={handleChange}
                placeholder="Contoh: Budi Santoso"
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.fullname ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
              {errors.fullname && <p className="text-rose-500 text-[11px] font-semibold">{errors.fullname}</p>}
            </div>

            {/* Tempat Lahir */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiMapPin className="text-blue-600 w-4 h-4" />
                Tempat Lahir
              </label>
              <input
                type="text"
                name="tempat_lahir"
                value={formData.tempat_lahir}
                onChange={handleChange}
                placeholder="Contoh: Jakarta"
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.tempat_lahir ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
            </div>

            {/* Tanggal Lahir */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiCalendar className="text-blue-600 w-4 h-4" />
                Tanggal Lahir
              </label>
              <input
                type="date"
                name="tanggal_lahir"
                value={formData.tanggal_lahir}
                onChange={handleChange}
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                  errors.tanggal_lahir ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiUser className="text-blue-600 w-4 h-4" />
                Jenis Kelamin (Gender)
              </label>
              <div className="flex gap-3 pt-1">
                {["Laki-laki", "Perempuan"].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setFormData({ ...formData, gender: g })}
                    className={`flex-1 py-2.5 rounded-2xl text-xs font-bold transition-all ${
                      formData.gender === g
                        ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            {/* Pendidikan */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiBookOpen className="text-blue-600 w-4 h-4" />
                Pendidikan Terakhir
              </label>
              <input
                type="text"
                name="pendidikan"
                value={formData.pendidikan}
                onChange={handleChange}
                placeholder="Contoh: S1 Farmasi / D3 Apoteker"
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.pendidikan ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiMail className="text-blue-600 w-4 h-4" />
                Alamat Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Contoh: budi@djayamedika.com"
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.email ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
              {errors.email && <p className="text-rose-500 text-[11px] font-semibold">{errors.email}</p>}
            </div>

            {/* No. HP */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiPhone className="text-blue-600 w-4 h-4" />
                Nomor Handphone
              </label>
              <input
                type="tel"
                name="no_hp"
                value={formData.no_hp}
                onChange={handleChange}
                placeholder="Contoh: 08123456789"
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.no_hp ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
              {errors.no_hp && <p className="text-rose-500 text-[11px] font-semibold">{errors.no_hp}</p>}
            </div>

            {/* Tanggal Bergabung */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiBriefcase className="text-blue-600 w-4 h-4" />
                Tanggal Bergabung (Join Date)
              </label>
              <input
                type="date"
                name="join_date"
                value={formData.join_date}
                onChange={handleChange}
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                  errors.join_date ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
            </div>

            {/* Penempatan Outlet */}
            <div className="space-y-2 md:col-span-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FaStore className="text-blue-600 w-4 h-4" />
                Penempatan Outlet
              </label>
              <select
                name="penempatan"
                value={formData.penempatan}
                onChange={handleChange}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              >
                <option value="">-- Pilih Penempatan Outlet --</option>
                {outlets.map((outlet) => (
                  <option key={outlet.id} value={outlet.id}>
                    {outlet.name} ({outlet.kota || "Pusat"})
                  </option>
                ))}
              </select>
            </div>

            {/* Alamat Lengkap */}
            <div className="space-y-2 md:col-span-2">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                <FiMapPin className="text-blue-600 w-4 h-4" />
                Alamat Tempat Tinggal
              </label>
              <textarea
                name="alamat"
                rows={3}
                value={formData.alamat}
                onChange={handleChange}
                placeholder="Isi alamat lengkap domisili..."
                className={`w-full p-3 bg-gray-50 border rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-semibold ${
                  errors.alamat ? "border-rose-400" : "border-gray-200"
                }`}
                required
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={() => navigate("/enterprise/karyawan")}
              className="px-5 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
            >
              Batal
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
            >
              <FiSave className="w-4 h-4" />
              <span>{loading ? "Menyimpan..." : "Simpan Karyawan"}</span>
            </motion.button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateKaryawan;
